import {
  GenericWorkflowAction,
  WorkflowCriteriaVariableSchemas,
  WorkflowFilterFieldConfigs,
  WorkflowOperators,
} from 'dashboard/models/workflow-schema';
import { ObjectSchemaMap } from 'dashboard/redux/slices/object-schemas';
import Action from 'lib/interfaces/Action';
import { JsonSchemaObject } from 'lib/json-schema';

interface WorkflowCriteriaVariableSchemasPayload {
  criteriaVariableSchemas: WorkflowCriteriaVariableSchemas;
}

interface WorkflowFilterFieldConfigsPayload {
  filterFieldConfigs: WorkflowFilterFieldConfigs;
}

interface WorkflowOperatorsPayload {
  operators: WorkflowOperators;
}

interface WorkflowActionsPayload {
  genericWorkflowActions: GenericWorkflowAction[];
}

interface WorkflowPrimitivesPayload {
  primitives: ObjectSchemaMap;
}

export enum WorkflowSchemaActionType {
  AppendCriteriaVariableSchemas = 'workflow-schema/APPEND_CRITERIA_VARIABLE_SCHEMAS',
  ClearCriteriaVariableSchemas = 'workflow-schema/CLEAR_CRITERIA_VARIABLE_SCHEMAS',
  SetCriteriaVariableSchemas = 'workflow-schema/SET_CRITERIA_VARIABLE_SCHEMAS',
  SetOperators = 'workflow-schema/SET_OPERATORS',
  SetWorkflowActions = 'workflow-schema/SET_WORKFLOW_ACTIONS',
  ClearSchema = 'workflow-schema/CLEAR_SCHEMA',
  SetFilterFieldConfigs = 'workflow-schema/SET_FILTER_FIELD_CONFIGS',
  SetPrimitives = 'workflow-schema/SET_PRIMITIVES',
}

export function appendWorkflowCriteriaVariableSchemas(
  criteriaVariableSchemas: WorkflowCriteriaVariableSchemasPayload['criteriaVariableSchemas'],
): Action<
  WorkflowSchemaActionType.AppendCriteriaVariableSchemas,
  WorkflowCriteriaVariableSchemasPayload
> {
  return {
    payload: {
      criteriaVariableSchemas,
    },
    type: WorkflowSchemaActionType.AppendCriteriaVariableSchemas,
  };
}

export function clearWorkflowCriteriaVariableSchemas(): Action<
  WorkflowSchemaActionType.ClearCriteriaVariableSchemas,
  Record<string, never>
> {
  return {
    payload: {},
    type: WorkflowSchemaActionType.ClearCriteriaVariableSchemas,
  };
}

export function setWorkflowCriteriaVariableSchemas(
  criteriaVariableSchemas: WorkflowCriteriaVariableSchemasPayload['criteriaVariableSchemas'],
): Action<
  WorkflowSchemaActionType.SetCriteriaVariableSchemas,
  WorkflowCriteriaVariableSchemasPayload
> {
  return {
    payload: {
      criteriaVariableSchemas,
    },
    type: WorkflowSchemaActionType.SetCriteriaVariableSchemas,
  };
}

export function setWorkflowFilterFieldConfigs(
  filterFieldConfigs: WorkflowFilterFieldConfigsPayload['filterFieldConfigs'],
): Action<WorkflowSchemaActionType.SetFilterFieldConfigs, WorkflowFilterFieldConfigsPayload> {
  return {
    payload: {
      filterFieldConfigs,
    },
    type: WorkflowSchemaActionType.SetFilterFieldConfigs,
  };
}

export function setWorkflowOperators(
  operators: WorkflowOperatorsPayload['operators'],
): Action<WorkflowSchemaActionType.SetOperators, WorkflowOperatorsPayload> {
  return {
    payload: {
      operators,
    },
    type: WorkflowSchemaActionType.SetOperators,
  };
}

export function setWorkflowActions(
  genericWorkflowActions: WorkflowActionsPayload['genericWorkflowActions'],
): Action<WorkflowSchemaActionType.SetWorkflowActions, WorkflowActionsPayload> {
  return {
    payload: {
      genericWorkflowActions,
    },
    type: WorkflowSchemaActionType.SetWorkflowActions,
  };
}

export function clearWorkflowSchema(): Action<WorkflowSchemaActionType.ClearSchema> {
  return { type: WorkflowSchemaActionType.ClearSchema };
}

export function setWorkflowPrimitives(
  primitives: Record<string, JsonSchemaObject>,
): Action<WorkflowSchemaActionType.SetPrimitives, WorkflowPrimitivesPayload> {
  return {
    payload: {
      primitives,
    },
    type: WorkflowSchemaActionType.SetPrimitives,
  };
}

export type WorkflowSchemaActions =
  | Action<
      WorkflowSchemaActionType.AppendCriteriaVariableSchemas,
      WorkflowCriteriaVariableSchemasPayload
    >
  | Action<WorkflowSchemaActionType.ClearCriteriaVariableSchemas, Record<string, never>>
  | Action<
      WorkflowSchemaActionType.SetCriteriaVariableSchemas,
      WorkflowCriteriaVariableSchemasPayload
    >
  | Action<WorkflowSchemaActionType.SetOperators, WorkflowOperatorsPayload>
  | Action<WorkflowSchemaActionType.SetWorkflowActions, WorkflowActionsPayload>
  | Action<WorkflowSchemaActionType.SetFilterFieldConfigs, WorkflowFilterFieldConfigsPayload>
  | Action<WorkflowSchemaActionType.ClearSchema>
  | Action<WorkflowSchemaActionType.SetPrimitives, WorkflowPrimitivesPayload>;
